import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
  FloatingPortal,
} from '@floating-ui/react';

interface SearchableDropdownProps {
  options: string[] | Array<{ value: string | number; label: string }>;
  value: string | number;
  onChange: (value: string | number) => void;
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  className?: string;
  truncate?: boolean;
  allowCustom?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  searchable = false,
  disabled = false,
  className = '',
  truncate = true,
  allowCustom = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const referenceRef = useRef<HTMLElement | null>(null);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            '--dropdown-max-height': `${Math.min(availableHeight - 8, 240)}px`,
          } as React.CSSProperties);
        },
      }),
    ],
  });

  const normalizedOptions = options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );

  const filteredOptions = searchable
    ? normalizedOptions.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : normalizedOptions;

  const displayLabel =
    normalizedOptions.find((opt) => opt.value === value)?.label ||
    (allowCustom && value ? String(value) : placeholder);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const referenceNode = referenceRef.current;
      const floatingNode = refs.floating.current;

      if (referenceNode?.contains(target) || floatingNode?.contains(target)) return;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [refs.floating]);

  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCustomSelect = () => {
    if (searchTerm.trim()) {
      onChange(searchTerm.trim());
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const setReferenceNode = (node: HTMLElement | null) => {
    referenceRef.current = node;
    refs.setReference(node);
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <div className="relative">
        {searchable && isOpen ? (
          <input
            ref={(node) => {
              inputRef.current = node;
              setReferenceNode(node);
            }}
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            disabled={disabled}
            className="input-field pr-10 focus:ring-2 focus:ring-primary focus:border-primary"
          />
        ) : (
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            ref={setReferenceNode}
            className={`input-field w-full flex items-center justify-between text-left ${
              disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''
            }`}
          >
            <span
              className={`${value ? 'text-gray-900' : 'text-gray-500'} ${truncate ? 'truncate' : ''}`}
            >
              {isOpen && searchable ? searchTerm : displayLabel}
            </span>
            <div className="flex items-center gap-1 pointer-events-none flex-shrink-0 ml-2">
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </button>
        )}
      </div>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-[1040] bg-white border border-primary/20 rounded-lg shadow-lg"
          >
            <ul
              className="overflow-y-auto"
              style={{ maxHeight: 'var(--dropdown-max-height, 240px)' }}
            >
              {filteredOptions.length > 0 ? (
                <>
                  {filteredOptions.map((option) => (
                    <li key={option.value}>
                      <button
                        type="button"
                        onClick={() => handleSelect(String(option.value))}
                        className={`w-full text-left px-4 py-2 transition-colors flex items-center justify-between ${
                          value === option.value
                            ? 'bg-primary/10 text-primary hover:bg-primary/10'
                            : 'hover:bg-primary/5'
                        }`}
                      >
                        <span>{option.label}</span>
                        {value === option.value && (
                          <span className="text-primary">✓</span>
                        )}
                      </button>
                    </li>
                  ))}
                  {allowCustom &&
                    searchTerm.trim() &&
                    !filteredOptions.find(
                      (opt) =>
                        opt.label.toLowerCase() === searchTerm.trim().toLowerCase()
                    ) && (
                      <li className="px-4 py-2 border-t border-gray-100">
                        <button
                          type="button"
                          onClick={handleCustomSelect}
                          className="text-primary hover:text-primary font-medium text-sm w-full text-left"
                        >
                          Use "{searchTerm.trim()}" as custom input
                        </button>
                      </li>
                    )}
                </>
              ) : (
                <li className="px-4 py-2 text-gray-500 text-sm">
                  {allowCustom && searchTerm.trim() ? (
                    <button
                      type="button"
                      onClick={handleCustomSelect}
                      className="text-primary font-medium text-sm w-full text-left"
                    >
                      Use "{searchTerm.trim()}" as custom input
                    </button>
                  ) : (
                    'No options found'
                  )}
                </li>
              )}
            </ul>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default SearchableDropdown;
